import {
  CustomersResponse,
  DeleteBulkCustomersResponse,
  ExportCustomersResponse,
  GetCustomerByIdResponse,
  GetESCustomersResponse,
  GetCustomerPointsByIdResponse,
  GetCustomerPointsHistoryByIdResponse,
  PutAdjustCustomerPointsResponse,
  PostESCustomerIdsResponse,
  GetESCustomerPaginationIdsResponse,
} from 'types/APIResponsesTypes';
import { APIClient } from 'services/helpers/api_helper';
import * as url from 'services/helpers/url_helper';
import {
  dedupeRequest,
  stableRequestKey,
} from 'services/helpers/requestDedupe';

import { AxiosResponse } from 'axios';
import {
  // CreateCustomerRequest,
  // UpdateCustomerRequest,
  CreateCustomerRequestWithPickupAddresses,
  GetCustomerResponseData,
  UpdateCustomerPointsRequest,
  UpdateCustomerRequestWithPickupAddresses,
} from 'types/customer/customerTypes';

const api = new APIClient();

export type CustomerSearchESFieldType =
  | 'name'
  | 'phone'
  | 'account_number'
  | 'email';

export interface CustomerESSearchParams {
  name?: string;
  phone?: string;
  account_number?: string;
  email?: string;
}

export interface CustomerESSearchIdsBody {
  page: number;
  limit: number;
  person_ids: number[];
  sort_by?: 'person_id' | 'account_number';
  sort_order?: 'asc' | 'desc';
}

export interface CustomerSimpleListBody {
  store_ids: number[];
  page: number;
  limit: number;
  person_ids?: number[];
  sort_by?: 'person_id' | 'account_number';
  sort_order?: 'asc' | 'desc';
}

export const getCustomers = async (
  store_ids: string,
  account_number = '',
  phone_number = '',
  email = '',
  name = '',
  page = 1,
  limit = 25,
  sort_order = '',
  sort_by = ''
) => {
  try {
    const response: Promise<CustomersResponse> = await api.get(
      url.GET_CUSTOMERS,
      {
        store_ids,
        account_number,
        phone_number,
        email,
        name,
        page,
        limit,
        sort_by,
        sort_order,
      }
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getSearchCustomersFromES = async (
  params: CustomerESSearchParams,
  limit = 20
) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    );
    const requestParams = { ...filteredParams, limit };
    const cacheKey = `es-customers:${stableRequestKey(requestParams)}`;

    const response: Promise<GetESCustomersResponse> = await dedupeRequest(
      cacheKey,
      () => api.get(url.GET_SEARCH_CUSTOMERS, requestParams)
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const postSearchCustomerIdsFromES = async (
  body: CustomerESSearchIdsBody
) => {
  try {
    const response: Promise<PostESCustomerIdsResponse> = await api.create(
      url.POST_SEARCH_CUSTOMER_IDS,
      body
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getSearchCustomerPaginationIdsFromES = async (
  params: CustomerESSearchParams,
  page = 1,
  limit = 25
) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
    );
    const response: Promise<GetESCustomerPaginationIdsResponse> = await api.get(
      url.GET_SEARCH_CUSTOMERS_PAGINATION_IDS,
      { ...filteredParams, page, limit }
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const postCustomersSimple = async (body: CustomerSimpleListBody) => {
  try {
    const response: Promise<CustomersResponse> = await api.create(
      url.POST_CUSTOMERS_SIMPLE,
      body
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getCustomerById = async (id: number) => {
  try {
    const response: Promise<GetCustomerByIdResponse> = await api.get(
      `${url.GET_CUSTOMER_BY_ID}${id}?pickup_address=1`
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const postAddCustomer = async (
  FormData: CreateCustomerRequestWithPickupAddresses
): Promise<any> => {
  try {
    const response: Promise<GetCustomerResponseData> = await api.create(
      url.POST_ADD_CUSTOMERS,
      FormData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const putUpdateCustomer = async (
  id: string,
  FormData: UpdateCustomerRequestWithPickupAddresses
) => {
  try {
    const response: Promise<GetCustomerResponseData> = await api.put(
      `${url.PUT_UPDATE_CUSTOMERS}${id}`,
      FormData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteBulkCustomers = async (customerIds: number[]) => {
  try {
    const body = {
      customer_ids: customerIds,
    };
    const response: AxiosResponse<DeleteBulkCustomersResponse> =
      await api.delete(url.DELETE_BULK_CUSTOMERS, {
        data: body,
      });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getCustomerPointsById = async (id: number) => {
  try {
    const response: Promise<GetCustomerPointsByIdResponse> = await api.get(
      `${url.GET_CUSTOMER_POINTS_BY_ID}${id}`
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const putAdjustCustomerPoints = async (
  id: number,
  body: UpdateCustomerPointsRequest
) => {
  try {
    const response: Promise<PutAdjustCustomerPointsResponse> = await api.put(
      `${url.PUT_ADJUST_CUSTOMER_POINTS}${id}`,
      body
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getExportCustomers = async (
  store_ids: string,
  account_number = '',
  phone_number = '',
  email = '',
  name = '',
  start: number,
  end: number,
  page = 1,
  limit = 25,
  sort_by = '',
  sort_order = ''
) => {
  try {
    const response: Promise<ExportCustomersResponse> = await api.get(
      url.GET_EXPORT_CUSTOMERS,
      {
        store_ids,
        account_number,
        phone_number,
        email,
        name,
        start,
        end,
        page,
        limit,
        sort_by,
        sort_order,
      }
    );

    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getPointsHistoryList = async (
  customer_id = 0,
  page = 1,
  limit = 8,
  sort_order = 'desc'
) => {
  const req = {
    limit: limit,
    page: page,
    sort_order: sort_order,
  };

  try {
    const response: Promise<GetCustomerPointsHistoryByIdResponse> =
      await api.get(
        `${url.GET_CUSTOMER_POINTS_HISTORY_BY_ID}${customer_id}`,
        req
      );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};
